// ============================================================
//  Token / session storage
//  Access tokens: sessionStorage (limits persistence).
//  Refresh tokens: HttpOnly cookie only — never stored in JS.
// ============================================================

const KEYS = {
    ACCESS_TOKEN: 'access_token',
    REFRESH_TOKEN: 'refresh_token',
    USER_ID: 'user_id',
    USER_DATA: 'user_data',
} as const

export interface StoredUserData {
    id?: string
    userId?: string
    fullName?: string
    firstName?: string
    lastName?: string
    email?: string
}

class LocalStorageManager {
    setApiKey(token: string): void {
        sessionStorage.setItem(KEYS.ACCESS_TOKEN, token)
        localStorage.removeItem(KEYS.ACCESS_TOKEN)
    }

    getApiKey(): string | null {
        return sessionStorage.getItem(KEYS.ACCESS_TOKEN) || localStorage.getItem(KEYS.ACCESS_TOKEN)
    }

    removeApiKey(): void {
        sessionStorage.removeItem(KEYS.ACCESS_TOKEN)
        localStorage.removeItem(KEYS.ACCESS_TOKEN)
    }

    setRefreshKey(_token: string): void {
        sessionStorage.removeItem(KEYS.REFRESH_TOKEN)
        localStorage.removeItem(KEYS.REFRESH_TOKEN)
    }

    getRefreshKey(): string | null {
        return null
    }

    removeRefreshKey(): void {
        sessionStorage.removeItem(KEYS.REFRESH_TOKEN)
        localStorage.removeItem(KEYS.REFRESH_TOKEN)
    }

    setUserId(userId: string): void {
        localStorage.setItem(KEYS.USER_ID, userId)
    }

    getUserId(): string | null {
        return localStorage.getItem(KEYS.USER_ID)
    }

    removeUserId(): void {
        localStorage.removeItem(KEYS.USER_ID)
    }

    setUserData(user: StoredUserData): void {
        localStorage.setItem(KEYS.USER_DATA, JSON.stringify(user))
    }

    getUserData(): StoredUserData | null {
        const rawUser = localStorage.getItem(KEYS.USER_DATA)
        if (!rawUser) return null

        try {
            return JSON.parse(rawUser) as StoredUserData
        } catch {
            return null
        }
    }

    removeUserData(): void {
        localStorage.removeItem(KEYS.USER_DATA)
    }

    isAuthenticated(): boolean {
        return !!this.getApiKey() && !!this.getUserId()
    }

    clearAuth(): void {
        this.removeApiKey()
        this.removeRefreshKey()
        this.removeUserId()
        this.removeUserData()
    }

    getAuthData(): { token: string | null; refreshToken: string | null; userId: string | null; user: StoredUserData | null } {
        return {
            token: this.getApiKey(),
            refreshToken: null,
            userId: this.getUserId(),
            user: this.getUserData(),
        }
    }
}

const LocalStorage = new LocalStorageManager()
export default LocalStorage
